import { CommonModule } from '@angular/common';
import { Component,Inject } from '@angular/core';
import { FormControl,FormGroup,ReactiveFormsModule,Validators } from '@angular/forms';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyDialog as MatDialog,MatLegacyDialogConfig as MatDialogConfig,MatLegacyDialogModule as MatDialogModule,MatLegacyDialogRef as MatDialogRef,MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Actions,ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom,Observable,of,switchMap } from 'rxjs';
import { map,startWith } from 'rxjs/operators';
import { TextareaModule } from '../../../components/templates/textarea/textarea.module';
import {
AccountsReceivableLedgerTemplateEntity
} from '../../../dave-data-module/entities/accounts-receivable-ledger-template.entity';
import { TravelZoneEntity } from '../../../dave-data-module/entities/travel-zone.entity';
import { AccountsReceivableLedgerTemplateResolver } from '../../../dave-data-module/guards/accounts-receivable-ledger-template.resolver';
import { TravelZoneResolver } from '../../../dave-data-module/guards/travel-zone.resolver';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { State } from '../../../dave-data-module/State';
import { TravelZoneActionTypes } from '../../../dave-data-module/State/actions/travel-zone.actions';
import { getAccountsReceivableLedgerTemplateById,getAccountsReceivableLedgerTemplatesFetched } from '../../../dave-data-module/State/selectors/accounting.selector';
import {
getTravelZoneById,
getTravelZonesActive,
getTravelZonesFetched
} from '../../../dave-data-module/State/selectors/travel-zone.selector';
import { NewBookingOptionComponent,NewBookingOptionComponentReturnType,NewOptionDialogData } from '../../../dave-reports/components/new-booking-option/new-booking-option.component';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import {
DaveSharedComponentsModule
} from '../../../dave-utils-module/dave-shared-components-module/dave-shared-components.module';
import { appMatDialogDefaultConfig } from '../../../helper/helper';
import { duplicationValidator } from '../../../helper/validation.helper';
import { LoadingService } from '../../../services/loading.service';

export interface EditTravelZoneComponentDialogData {
    travelZoneId?: number;
}
export type EditTravelZoneComponentDialogReturnData = TravelZoneEntity | undefined;

@Component({
    selector: 'app-edit-travel-zone',
    standalone: true,
    imports: [CommonModule, MatDialogModule, TextareaModule, AppButtonModule, FontAwesomeModule, MatTooltipModule, MatChipsModule, MatLegacyInputModule, ReactiveFormsModule],
    templateUrl: './edit-travel-zone.component.html',
    styleUrls: ['./edit-travel-zone.component.scss'],
})
export class EditTravelZoneComponent {
    public static readonly DefaultConfig: MatDialogConfig = {
        ...appMatDialogDefaultConfig,
        minWidth: '30rem',
    };
    headline = TravelZoneEntity.EntityName + ' anlegen';
    travelZoneId: number;
    formGroup: FormGroup<{
        Name: FormControl<string | null>;
        TravelZoneNumber: FormControl<number | null>;
        Bearbeitungsschluessel: FormControl<number | null>;
        Amount: FormControl<number | null>;
        PercentAmountFromTravelZone: FormControl<number | null>;
        Description: FormControl<string | null>;
        arlTemplate: FormControl<number | null>;
    }>;
    selectedArlTemplate$: Observable<AccountsReceivableLedgerTemplateEntity>;
    constructor(
        @Inject(MAT_DIALOG_DATA) data: EditTravelZoneComponentDialogData,
        travelZoneRes: TravelZoneResolver,
        arlTemplateRes: AccountsReceivableLedgerTemplateResolver,
        private store: Store<State>,
        public ls: LoadingService,
        private actions$: Actions,
        private dialogRef: MatDialogRef<EditTravelZoneComponent, EditTravelZoneComponentDialogReturnData>,
        private dialog: MatDialog,
    ) {
        if (data?.travelZoneId) {
            this.travelZoneId = data?.travelZoneId;
            this.headline = TravelZoneEntity.EntityName + ' bearbeiten';
        }
        this.initForm();
        firstValueFrom(store.select(getTravelZonesFetched)).then((fetched) => {
            if (!fetched) {
                travelZoneRes.resolve();
            }
        });
        firstValueFrom(store.select(getAccountsReceivableLedgerTemplatesFetched)).then((fetched) => {
            if (!fetched) {
                arlTemplateRes.resolve();
            }
        });
    }
    private async initForm() {
        let tz: TravelZoneEntity;
        if (this.travelZoneId) {
            tz = await firstValueFrom(getFetched$(this.store, getTravelZonesFetched, getTravelZoneById({ id: this.travelZoneId })));
        }
        const options = await firstValueFrom(getFetched$(this.store, getTravelZonesFetched, getTravelZonesActive));
        this.formGroup = new FormGroup({
            Name: new FormControl<string>(tz?.Name, [Validators.required, duplicationValidator(options.map((o) => o.Name).filter((n) => n && n !== tz?.Name))]),
            TravelZoneNumber: new FormControl<number>(tz?.TravelZoneNumber),
            Bearbeitungsschluessel: new FormControl<number>(tz?.Bearbeitungsschluessel),
            Amount: new FormControl<number>(tz?.Amount/100),
            PercentAmountFromTravelZone: new FormControl<number>(tz?.PercentAmountFromTravelZone),
            Description: new FormControl<string>(tz?.Description),
            arlTemplate: new FormControl<number>(tz?.ArlTemplateId),
        });
        this.selectedArlTemplate$ = this.formGroup.controls.arlTemplate.valueChanges.pipe(
            startWith(null),
            map(() => this.formGroup.controls.arlTemplate.value),
            switchMap((id) => (id ? getFetched$(this.store, getAccountsReceivableLedgerTemplatesFetched, getAccountsReceivableLedgerTemplateById({ id })) : of(null))),
        );
    }
    protected onSelectArlClick() {
        import('../../../dave-reports/components/new-booking-option/new-booking-option.component').then((m) => {
            this.dialog
                .open<NewBookingOptionComponent, NewOptionDialogData, NewBookingOptionComponentReturnType>(m.NewBookingOptionComponent, {
                    ...m.NewBookingOptionComponent.DefaultConfig,
                    data: {
                        onlyOne: true,
                        onlyReturnTemplateIds: true,
                        addedTemplateIds: this.formGroup.value.arlTemplate ? [this.formGroup.value.arlTemplate] : null,
                    },
                })
                .afterClosed()
                .subscribe((ret: number[] | undefined) => {
                    if (ret /*?.length*/) {
                        this.formGroup.controls.arlTemplate.setValue(ret[0]);
                        this.formGroup.controls.arlTemplate.markAsDirty();
                    }
                });
        });
    }
    submit() {
        if (this.travelZoneId) {
            this.ls.startLoading('app-edit-travel-zone_edit');
            firstValueFrom(this.actions$.pipe(ofType(TravelZoneActionTypes.ChangeSuccess, TravelZoneActionTypes.ChangeFailure))).then((action) => {
                this.ls.endLoading('app-edit-travel-zone_edit');
                if (action.type === TravelZoneActionTypes.ChangeSuccess.type && action.Payload instanceof TravelZoneEntity) {
                    this.dialogRef.close(action.Payload);
                }
            });
            this.store.dispatch(
                TravelZoneActionTypes.ChangeRequest({
                    Payload: {
                        Id: this.travelZoneId.toString(),
                        Name: this.formGroup.value.Name,
                        Amount: this.formGroup.value.Amount ? (this.formGroup.value.Amount *100).toString() : null,
                        ArlTemplateId: this.formGroup.value.arlTemplate ? this.formGroup.value.arlTemplate.toString() : null,
                        Description: this.formGroup.value.Description,
                        TravelZoneNumber: this.formGroup.value.TravelZoneNumber ? this.formGroup.value.TravelZoneNumber.toString() : null,
                        Bearbeitungsschluessel: this.formGroup.value.Bearbeitungsschluessel ? this.formGroup.value.Bearbeitungsschluessel.toString() : null,
                        PercentAmountFromTravelZone: this.formGroup.value.PercentAmountFromTravelZone ? this.formGroup.value.PercentAmountFromTravelZone : null,
                    },
                }),
            );
        } else {
            this.ls.startLoading('app-edit-travel-zone_add');
            firstValueFrom(this.actions$.pipe(ofType(TravelZoneActionTypes.AddSuccess, TravelZoneActionTypes.AddFailure))).then((action) => {
                this.ls.endLoading('app-edit-travel-zone_add');
                if (action.type === TravelZoneActionTypes.AddSuccess.type && action.Payload instanceof TravelZoneEntity) {
                    this.dialogRef.close(action.Payload);
                }
            });
            this.store.dispatch(
                TravelZoneActionTypes.AddRequest({
                    Payload: {
                        Name: this.formGroup.value.Name,
                        ArlTemplateId: this.formGroup.value.arlTemplate ? this.formGroup.value.arlTemplate.toString() : null,
                        Description: this.formGroup.value.Description,
                        Amount: this.formGroup.value.Amount ? (this.formGroup.value.Amount *100).toString() : null,
                        TravelZoneNumber: this.formGroup.value.TravelZoneNumber ? this.formGroup.value.TravelZoneNumber.toString() : null,
                        Bearbeitungsschluessel: this.formGroup.value.Bearbeitungsschluessel ? this.formGroup.value.Bearbeitungsschluessel.toString() : null,
                        PercentAmountFromTravelZone: this.formGroup.value.PercentAmountFromTravelZone ? this.formGroup.value.PercentAmountFromTravelZone : null,
                    },
                }),
            );
        }
    }
}
